import { ApolloError } from '@apollo/client';
import {
  GetLaneHistoryQuery,
  QuoteHistorySummaryFragment,
  ShipmentHistorySummaryFragment,
  useGetLaneHistoryQuery,
} from '@shared/generated/graphql';
import { Quote } from '@shared/types/quote';
import { LaneHistoryFilters } from './useLaneHistoryFilters';
import { useGetLaneShipments } from './useLaneShipments';

type UseGetQuotesState = {
  quotes: QuoteHistorySummaryFragment[];
  shipments: ShipmentHistorySummaryFragment[];
  loading?: boolean;
  error?: ApolloError;
};

const getHistory = (quote: Quote, data?: GetLaneHistoryQuery) => {
  // NOTE(parlato): Do not show a route history if the quote has no
  // geocoordinates
  if (!quote.routeGeoCoordinates) return [];

  return (
    data?.quotes.edges
      .map((edge) => edge.node)
      .filter((q) => q.id !== quote.id && Boolean(q.customerRate)) ?? []
  );
};

export const useLaneHistory = (
  quote: Quote,
  filters: LaneHistoryFilters
): UseGetQuotesState => {
  const {
    data,
    loading: loadingQuotes,
    error,
  } = useGetLaneHistoryQuery({
    variables: {
      input: {
        organizationIds: [quote.organization.id],
        originatorIds: filters.originatorIds,
        statuses: filters.statuses,
        laneFilter: filters.laneFilter,
        equipmentTypes: filters.equipmentTypes,
      },
      first: 20,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30 * 1000,
  });

  const quotes = getHistory(quote, data);

  const { shipments, loading: loadingShipments } = useGetLaneShipments({
    equipmentTypes: filters.equipmentTypes,
    laneFilter: filters.laneFilter,
  });

  const loading = loadingQuotes || loadingShipments;

  return {
    quotes,
    shipments,
    loading,
    error,
  };
};
